import { useState } from 'react';

import { Timeline as MuiTimeline, timelineItemClasses } from '@mui/lab';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useQuery } from 'react-query';
import {
  fetchTimelineOperationData,
  getTimelineFilters,
} from 'src/services/convocations';
import { MetaResumoAssembleia } from 'src/types/convocation-summary';

import { DesktopTimeline, YearSeparatorDesktop } from './Desktop';
import { TimelineFilters } from './Filters';
import { MobileTimeline, YearSeparatorMobile } from './Mobile';

type TimelineProps = {
  hashId: string;
};

type GroupedData = {
  year: number;
  items: MetaResumoAssembleia[];
};

type Filters = {
  startDate: string | null;
  endDate: string | null;
  category: string;
  species: string;
  type: string;
};

const groupDataByYear = (data: MetaResumoAssembleia[]) => {
  const grouped = data.reduce(
    (acc, item) => {
      const year = new Date(item.datareferencia).getFullYear();
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(item);
      return acc;
    },
    {} as Record<number, MetaResumoAssembleia[]>,
  );

  return Object.keys(grouped)
    .sort((a, b) => parseInt(b) - parseInt(a))
    .map((year) => ({
      year: parseInt(year),
      items: grouped[parseInt(year)].sort(
        (a, b) =>
          new Date(b.datareferencia).getTime() -
          new Date(a.datareferencia).getTime(),
      ),
    }));
};

export function Timeline({ hashId }: TimelineProps) {
  const [filters, setFilters] = useState<Filters>({
    startDate: null,
    endDate: null,
    category: '',
    species: '',
    type: '',
  });
  const [groupedData, setGroupedData] = useState<GroupedData[]>([]);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );

  const { data: filtersData, isLoading: isLoadingFilters } = useQuery({
    queryKey: ['filtersData', hashId],
    queryFn: () => getTimelineFilters(hashId),
  });

  const { isLoading } = useQuery({
    queryKey: ['timeline-data', hashId, filters],
    queryFn: () =>
      fetchTimelineOperationData(
        hashId,
        filters.type,
        filters.category,
        filters.species,
        filters.startDate ?? '',
        filters.endDate ?? '',
      ),
    onSuccess(data) {
      const groupedData = groupDataByYear(data.data);
      setGroupedData(groupedData);
    },
    enabled: !!hashId && !isLoadingFilters,
  });

  return (
    <Box>
      <Box display="flex" width="100%" flexDirection="column" gap={2}>
        <TimelineFilters
          filtersData={
            filtersData ?? {
              types: [],
              categories: [],
              species: [],
              markets: [],
            }
          }
          filters={filters}
          setFilters={setFilters}
        />

        {isMobile && !isLoading ? (
          <Box sx={{ mt: 3 }}>
            {groupedData.length > 0 ? (
              groupedData.map(({ year, items }) => (
                <Box key={year}>
                  {items.map((item) => (
                    <MobileTimeline key={item.id} item={item} />
                  ))}
                  <YearSeparatorMobile year={year} />
                </Box>
              ))
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%">
                <Typography variant="h6">Não há dados para exibir</Typography>
              </Box>
            )}
          </Box>
        ) : !isLoading ? (
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            alignItems="flex-start"
            sx={{ maxHeight: '720px', overflowY: 'auto' }}>
            <MuiTimeline
              position="right"
              sx={{
                width: '100%',
                [`& .${timelineItemClasses.root}:before`]: {
                  flex: 0,
                  padding: 0,
                },
              }}>
              {groupedData.length > 0 ? (
                groupedData.map(({ year, items }) => (
                  <Box key={year}>
                    {items.map((item) => (
                      <DesktopTimeline key={item.id} item={item} />
                    ))}
                    <YearSeparatorDesktop year={year} />
                  </Box>
                ))
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    p: 2,
                  }}>
                  <Typography variant="h6">Não há dados para exibir</Typography>
                </Paper>
              )}
            </MuiTimeline>
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%">
            <CircularProgress color="primary" />
          </Box>
        )}
      </Box>
      <Alert severity="info">
        <b>Aviso</b>: Estes resumos foram gerados por IA e podem não refletir
        todos os detalhes dos documentos originais. Recomenda-se consultar o
        documento completo para informações precisas.
      </Alert>
    </Box>
  );
}
